import React, { useEffect, useState } from "react";
import moment from "moment";
import "moment-timezone";

// Mui
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import RefreshIcon from "@mui/icons-material/Refresh";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";

// Custom
import CustomToast from "../toast/CustomToast";
import CustomDataTable from "../table/CustomTable";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { ToastState, WorkOrderV2T, WorkOrderResponseV2 } from "../../types";
import { fetchGETRequest } from "../../utils/services";
import { setWorkOrderHistory } from "../../redux/features/BillingDeterminantSlice";
import { isUUID } from "../../utils/helper";

const COLUMNS = [
  "Work Order No.",
  "Type",
  "Reason",
  "Status",
  "Requested At",
  "Completed At",
];

export const ConsumerWorkOrderHistory = ({ 
  meterId, 
  activeTab 
}: { 
  meterId?: string; 
  activeTab?: string; 
}) => {
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "info",
  });
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });

  const dispatch = useAppDispatch();
  const { workOrderHistory, selectedMeter } = useAppSelector(
    (state) => state.billing,
  );

  const getStatusDetails = (status: string) => {
    const s = status?.toUpperCase() || "";
    let label = status || "Unknown";
    let color = "#1064ED"; // Default blue
    let bgColor = "#e3f1fa";

    if (s === "COMPLETED" || s === "SUCCESS") {
      color = "#0cb254";
      bgColor = "#e1f5d7";
    } else if (s === "FAILED" || s === "REJECTED") {
      color = "#F65B5B";
      bgColor = "#fff1f1";
    } else if (s === "PENDING" || s === "IN PROGRESS") {
      color = "#FDA81F";
      bgColor = "#fff8e1";
    }

    return { label, color, bgColor };
  };

  const getTypeDetails = (type: string) => {
    const t = type?.toUpperCase() || "";
    let color = "#1064ED";
    let bgColor = "#e3f1fa";

    if (t.includes("DISCONNECT")) {
      color = "#F65B5B";
      bgColor = "#fff1f1";
    } else if (t.includes("CONNECT")) {
      color = "#0cb254";
      bgColor = "#e1f5d7";
    }

    return { label: type, color, bgColor };
  };

  const fetchWorkOrders = async (page: number = 0, pageSize: number = 10) => {
    const targetMeter = meterId || selectedMeter;
    if (!targetMeter) {
      return;
    }

    if (!isUUID(targetMeter)) {
      return;
    }

    setLoading(true);
    try {
      // V2 API is 1-based, our table is 0-based
      const response = await fetchGETRequest<WorkOrderResponseV2>(
        `v2/work-orders?MeterId=${targetMeter}&Page=${page + 1}&PageSize=${pageSize}`,
        {},
      );

      if (response?.success) {
        dispatch(setWorkOrderHistory(response.data || []));
        setPagination({
          currentPage: page,
          pageSize: pageSize,
          totalCount: response.totalCount || 0,
          totalPages: response.totalPages || 0,
        });
      } else {
        dispatch(setWorkOrderHistory([]));
      }
    } catch (error) {
      console.error("Error fetching work orders:", error);
      setToast({
        message: "Failed to fetch work order history.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "4") {
      fetchWorkOrders(0, pagination.pageSize);
    }
  }, [meterId, selectedMeter, activeTab]);

  const rowsContent = workOrderHistory?.map((item: WorkOrderV2T) => {
    const { label, color, bgColor } = getStatusDetails(item.status);
    const typeDetails = getTypeDetails(item.workOrderType);
    
    return (
      <TableRow key={item.id}>
        <TableCell>
          <Typography variant="tableBody" fontWeight={600}>
            {item.workOrderNumber || "N/A"}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={typeDetails.label}
            size="small"
            sx={{
              backgroundColor: typeDetails.bgColor,
              color: typeDetails.color,
              fontWeight: 600,
              fontSize: "11px",
              border: `1px solid ${typeDetails.color}`,
            }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="tableBody" sx={{ maxWidth: "200px" }}>
            {item.reason || "N/A"}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={label}
            size="small"
            sx={{
              backgroundColor: bgColor,
              color: color,
              fontWeight: 600,
              fontSize: "11px",
              border: `1px solid ${color}`,
            }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="tableBody">
            {item.createdAt
              ? moment(item.createdAt).format("DD/MM/YYYY HH:mm:ss")
              : "N/A"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="tableBody">
            {item.completedDate
              ? moment(item.completedDate).format("DD/MM/YYYY HH:mm:ss")
              : "N/A"}
          </Typography>
        </TableCell>
      </TableRow>
    );
  });

  return (
    <Box sx={{ p: 2 }}>
      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />

      <Card
        sx={{
          marginTop: "20px",
          height: "83vh",
          display: "flex",
          flexDirection: "column",
          borderWidth: "1px",
          borderColor: "#E0E0E0",
          boxShadow: "none",
        }}
      >
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="body1" fontWeight={"600"}>
                Work Order History
              </Typography>
              <IconButton onClick={() => fetchWorkOrders(pagination.currentPage, pagination.pageSize)}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          }
        />
        <Divider />
        <CardContent sx={{ flex: 1, p: 0, overflow: "hidden" }}>
          <CustomDataTable
            columns={COLUMNS}
            rowsContent={rowsContent}
            isLoading={loading}
            pagination
            totalCount={pagination.totalCount}
            page={pagination.currentPage}
            rowsPerPage={pagination.pageSize}
            onPageChange={(_, newPage) => fetchWorkOrders(newPage, pagination.pageSize)}
            onRowsPerPageChange={(event) => {
              const newSize = parseInt(event.target.value, 10);
              fetchWorkOrders(0, newSize);
            }}
            Actions={false}
          />
        </CardContent>
      </Card>
    </Box>
  );
};
